import * as forge from 'node-forge';
import { InboxItem } from '../models/card-v2.model';
import { Ad, aesCbcDecrypt, openSeal, rsaOpenJson } from './envelopes';
import { KeyRing, RingAlg } from './key-ring';
import { parsePqSalt, unb64, unwrapCardKey } from './wrap';

/**
 * 14f: gelen kutusu ogesini acik bayta ceviren panel acicisi (mobil inbox_opener.dart portu).
 * Acilamayan (bicim/MAC/bilinmeyen anahtar surumu) `locked`, bu surumun isi olmayan `skip`.
 *
 * Panele ozgu iki fark:
 *  - EnterpriseUserInfo (kurum kopyasi) `plain` olarak yerlesir: RSA ile cozulen metin aynen.
 *  - GroupSharedCard grup anahtari gerektirir; panel grup zarfi yazmaz, `locked` yerlestirir
 *    (oge sunucuda okunur kalir; mobil grup dondurucu sonra yazar).
 */
export type Opened =
  | { kind: 'plain'; bytes: Uint8Array; ad: string }
  | { kind: 'text'; text: string }
  | { kind: 'locked'; reason: string }
  | { kind: 'skip'; reason: string };

const ALGS = new Set(['pq', 'rsa', 'plain']);

/** Mobil tablo ile ayni (tur, alg) ciftleri; tabloda olmayan cift atlanir. */
const ALLOWED: Record<string, readonly string[]> = {
  SharedCard: ['pq', 'rsa'],
  SharedCardTags: ['plain'],
  GroupMemberKey: ['pq', 'rsa'],
  UserArchiveKey: ['rsa'],
  CardEventName: ['pq', 'rsa'],
  ShareInfo: ['pq', 'rsa'],
  EnterpriseUserInfo: ['rsa'],
  GroupSharedCard: ['rsa'],
  FormResponse: ['pq', 'rsa'],
  FormResponseCompany: ['pq', 'rsa'],
};

const utf8 = (s: string): Uint8Array => new TextEncoder().encode(s);

/** Mobil CardMetaCodec: virgulle ayrik etiketler -> `{"tags":[...]}` (kirpilmis, bos yok, tekrar yok). */
export function encodeTags(tagString: string): Uint8Array {
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const raw of tagString.split(',')) {
    const t = raw.trim();
    if (!t || seen.has(t.toLowerCase())) continue;
    seen.add(t.toLowerCase());
    tags.push(t);
  }
  return utf8(JSON.stringify({ tags }));
}

export class InboxOpener {
  private readonly rsa: string[];

  constructor(private readonly ring: KeyRing) {
    this.rsa = ring.rsaPems;
  }

  private readonly privFor = (kv: number): Uint8Array | null =>
    this.ring.byVersion(RingAlg.pq, kv)?.seed ?? null;

  async open(it: InboxItem): Promise<Opened> {
    const c = it.context ?? { alg: '' };
    if (!ALGS.has(c.alg)) return { kind: 'skip', reason: 'alg' };
    const allowed = ALLOWED[it.type];
    if (!allowed) return { kind: 'skip', reason: 'tur' };
    if (!allowed.includes(c.alg)) return { kind: 'skip', reason: 'tur-alg' };
    const lock = (b: Uint8Array | null, ad: string): Opened =>
      b ? { kind: 'plain', bytes: b, ad } : { kind: 'locked', reason: 'acilamadi' };
    try {
      switch (it.type) {
        case 'SharedCard': {
          const id = c.cardId;
          if (!id || it.keyStamp == null) return { kind: 'skip', reason: 'baglam' };
          const b = c.alg === 'pq'
            ? (isSeal(it.value) ? await this.sealed(it.value, Ad.card(id)) : await this.pqKey(it.value))
            : this.rsaCardSalt(it.value);
          return lock(b, Ad.card(id));
        }
        case 'SharedCardTags': {
          const id = c.cardId;
          if (!id) return { kind: 'skip', reason: 'baglam' };
          return { kind: 'plain', bytes: encodeTags(it.value), ad: Ad.cardMeta(id) };
        }
        case 'GroupMemberKey': {
          const g = c.groupId, kv = c.keyVersion;
          if (!g || kv == null) return { kind: 'skip', reason: 'baglam' };
          const b = c.alg === 'pq' ? await this.pqKey(it.value) : this.rsaMaterial(it.value);
          return lock(b, Ad.groupMemberKey(g, kv));
        }
        case 'UserArchiveKey': {
          const kv = c.keyVersion;
          if (kv == null) return { kind: 'skip', reason: 'baglam' };
          return lock(this.rsaMaterial(it.value), Ad.userArchiveKey(kv));
        }
        case 'CardEventName': {
          const b = c.alg === 'pq' ? await this.sealed(it.value, Ad.cardEventName) : this.rsaMaterial(it.value);
          return lock(b, Ad.cardEventName);
        }
        case 'ShareInfo': {
          const id = c.cardId;
          if (!id) return { kind: 'skip', reason: 'baglam' };
          const b = c.alg === 'pq' ? await this.sealed(it.value, Ad.shareInfo(id)) : this.rsaShareInfo(it.value, c.data);
          return lock(b, Ad.shareInfo(id));
        }
        case 'EnterpriseUserInfo': {
          const text = this.rsaText(it.value);
          return text ? { kind: 'text', text } : { kind: 'locked', reason: 'acilamadi' };
        }
        case 'FormResponse':
        case 'FormResponseCompany': {
          const key = c.alg === 'pq' ? await this.pqKey(it.value) : this.rsaMaterial(it.value);
          if (!key || key.length !== 32) {
            key?.fill(0);
            return { kind: 'locked', reason: 'acilamadi' };
          }
          return lock(key, Ad.formResponse(it.id));
        }
        case 'GroupSharedCard':
          return { kind: 'locked', reason: 'grup-zarfi-mobil' };
      }
    } catch {
      // Bicim bozuk (base64/JSON/uzunluk/GCM): kilitli. Icerik loglanmaz.
      return { kind: 'locked', reason: 'bicim' };
    }
    return { kind: 'skip', reason: 'tur' };
  }

  private async pqKey(value: string): Promise<Uint8Array | null> {
    const salt = parsePqSalt(value);
    const seed = this.privFor(salt.keyVersion);
    if (!seed) return null;
    try {
      return await unwrapCardKey(seed, salt.kem, salt.wrapped);
    } finally {
      seed.fill(0);
    }
  }

  private async sealed(value: string, ad: string): Promise<Uint8Array | null> {
    const r = await openSeal(value, this.privFor, ad);
    return r.state === 'ok' && r.bytes ? r.bytes : null;
  }

  /** `{"key": b64}` (grup/arsiv/etkinlik adi). */
  private rsaMaterial(value: string): Uint8Array | null {
    const k = rsaOpenJson(this.rsa, value)?.j['key'];
    return typeof k === 'string' ? unb64(k) : null;
  }

  /** SharedCard.Salt: v2 `{"key"}` -> 32 B; v1 `{"Key","IV"}` -> JSON aynen. */
  private rsaCardSalt(value: string): Uint8Array | null {
    const r = rsaOpenJson(this.rsa, value);
    if (!r) return null;
    const k = r.j['key'];
    if (typeof k === 'string') {
      const b = unb64(k);
      return b.length === 32 ? b : null;
    }
    if (typeof r.j['Key'] === 'string' && typeof r.j['IV'] === 'string') return utf8(r.raw);
    return null;
  }

  /** ShareInfo rsa: `{"Key","IV"}` + `context.data` (AES-CBC, base64). */
  private rsaShareInfo(value: string, data: string | null | undefined): Uint8Array | null {
    if (!data) return null;
    const j = rsaOpenJson(this.rsa, value)?.j;
    const k = j?.['Key'], iv = j?.['IV'];
    if (typeof k !== 'string' || typeof iv !== 'string') return null;
    return aesCbcDecrypt(k, iv, data);
  }

  /**
   * Kurum kopyasi: RSA-OAEP ile sarili `{NameSurname, Email}`. Panel eskiden utf8 cozmeden
   * okurdu (decryptRSA); once utf8 denenir, olmazsa ham ikili metin aynen alinir.
   */
  private rsaText(value: string): string | null {
    let bytes: string;
    try {
      bytes = forge.util.decode64(value);
    } catch {
      return null;
    }
    for (const pem of this.rsa) {
      let raw: string;
      try {
        raw = forge.pki.privateKeyFromPem(pem).decrypt(bytes, 'RSA-OAEP');
      } catch {
        continue;
      }
      try {
        return forge.util.decodeUtf8(raw);
      } catch {
        return raw;
      }
    }
    return null;
  }
}

function isSeal(value: string): boolean {
  const j = JSON.parse(value) as unknown;
  return !!j && typeof j === 'object' && 'ct' in (j as Record<string, unknown>);
}
